import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { ProfileContext } from "./ProfileContext";
import type { ProfileState } from "./ProfileContext";
import { gymClient } from "../apiClient/gymClient";
import { useAuth } from "../auth/useAuth";
import type { GymProfile, ProfileInput } from "../types/gym.types";

/**
 * Fetches the current user's profile once they're authenticated and shares it
 * across the app, so the gate, the form, and (later) the slots badge all read
 * the same copy — and a save updates them together without a refetch.
 */
export function ProfileProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [profile, setProfile] = useState<GymProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setProfile(await gymClient.getProfile());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load profile.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return; // wait for auth to resolve before deciding
    if (!isAuthenticated) {
      setProfile(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    void refresh();
  }, [authLoading, isAuthenticated, refresh]);

  const save = useCallback(async (input: ProfileInput) => {
    const saved = await gymClient.saveProfile(input);
    setProfile(saved);
    setError(null);
    return saved;
  }, []);

  const value = useMemo<ProfileState>(
    () => ({
      profile,
      isLoading,
      error,
      isComplete: profile !== null && profile.profileCompletedAt !== null,
      refresh,
      save,
    }),
    [profile, isLoading, error, refresh, save],
  );

  return <ProfileContext value={value}>{children}</ProfileContext>;
}
